import { MorningLight, glass } from "./leadsKit";

/**
 * Page stats for list pages (Leads, Patients) — design: components/crm/StatCards.tsx.
 * Glass over Morning light, so the strip owns its own <MorningLight />. The first
 * card is the lead stat: wider, with a 14-day trend line. Optional `highlight`
 * puts one card on sky (one per screen).
 *
 * Stat: { id, label, value, note?, trend? (daily counts, oldest first),
 *         highlight?, onClick?, actionLabel? }
 */

function Sparkline({ values }) {
  if (values.length < 2) return null;
  const w = 160;
  const h = 44;
  const max = Math.max(1, ...values);
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => [Math.round(i * step), Math.round(h - 4 - (v / max) * (h - 10))]);
  const line = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden className="min-w-0 w-full max-w-40 flex-1 overflow-visible">
      <polygon points={`0,${h} ${line} ${w},${h}`} className="fill-sky/25" />
      <polyline points={line} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" className="stroke-sky-deep" />
      <circle cx={last[0]} cy={last[1]} r={4} className="fill-sun stroke-ink" strokeWidth={1.5} />
    </svg>
  );
}

export function StatCards({ stats, label, equalColumns = false }) {
  return (
    <section aria-label={label} className="relative isolate">
      <MorningLight className="-inset-x-10 -top-24 h-[300px]" />
      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${equalColumns ? (stats.length === 5 ? "xl:grid-cols-5" : "xl:grid-cols-4") : "xl:grid-cols-[1.6fr_repeat(3,minmax(0,1fr))]"}`}>
        {stats.map((s, i) => {
          const body = (
            <>
              <span className={`text-small font-medium ${s.highlight === "sky" ? "text-ink/80" : "text-muted"}`}>{s.label}</span>
              <div className={`mt-3 flex min-w-0 items-end gap-2 ${equalColumns ? "justify-center" : "justify-between"}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-h1 font-semibold tabular-nums tracking-tight">{s.value}</div>
                  {s.note && <div className={`mt-1.5 text-small ${s.highlight === "sky" ? "text-ink/75" : "text-muted"} ${stats.length === 5 ? "" : "whitespace-nowrap"}`}>{s.note}</div>}
                </div>
                {i === 0 && s.trend && <Sparkline values={s.trend} />}
              </div>
            </>
          );
          const cls = `animate-nav-in flex flex-col p-5 ${equalColumns ? "items-center text-center" : "text-left"} ${s.highlight === "sky" ? "rounded-lg bg-[image:var(--gradient-sky)] text-ink shadow-soft" : s.highlight ? "rounded-lg bg-sky-soft shadow-soft ring-1 ring-sky/40" : glass}`;
          return s.onClick ? (
            <button
              key={s.id}
              type="button"
              onClick={s.onClick}
              aria-label={s.actionLabel ? `${s.label}: ${typeof s.value === "object" ? "loading" : s.value}. ${s.actionLabel}` : undefined}
              style={{ animationDelay: `${i * 60}ms` }}
              className={`${cls} cursor-pointer outline-none hover:ring-1 hover:ring-ink/15 focus-visible:ring-2 focus-visible:ring-ink/25`}
            >
              {body}
            </button>
          ) : (
            <div key={s.id} style={{ animationDelay: `${i * 60}ms` }} className={cls}>
              {body}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Daily counts for the last `days` days ending on `today` (inclusive). */
export function dailyCounts(timestamps, today, days = 14, dayMs = 86_400_000) {
  const start = today - (days - 1) * dayMs;
  const out = new Array(days).fill(0);
  for (const t of timestamps) {
    const idx = Math.floor((t - start) / dayMs);
    if (idx >= 0 && idx < days) out[idx]++;
  }
  return out;
}
